import { CustomMessageRepository } from '../../domain/repositories/CustomMessageRepository'
import { CustomMessageModel } from '../models/CustomMessageModel'
import { CustomMessageFilterEntity } from '../../domain/entities/CustomMessageFilterEntity'
import { CustomMessagesResponseModel } from '../models/CustomMessagesResponseModel'
import { CustomMessageFilterModel } from '../models/CustomMessageFilterModel'
import type { CustomMessagesResponseEntity } from '../../domain/entities/CustomMessagesResponseEntity'
import type { CustomMessageEntity } from '../../domain/entities/CustomMessageEntity'
import { UserMiniModel } from '@/features/user/data/models/UserMiniModel'
import type { UserMiniEntity } from '@/features/user/domain/entities/UserMiniEntity'
import type { CreateUpdateCustomMessageEntity } from '../../domain/entities/CreateUpdateCustomeMessageEntity'
import { CreateUpdateCustomMessageModel } from '../models/CreateUpdateCustomMessageModel'
import { CustomMessagesService, type PostCustomMessageRequestAdmin, type PutCustomMessageDistrictsRequestAdmin, type PutCustomMessageRegionsRequestAdmin, type PutCustomMessageRequestAdmin } from '@/backend/market'
import type { CustomMessageRegionEntity } from '../../domain/entities/CustomMessageRegionEntity'
import { CustomMessageRegionModel } from '../models/CustomMessageRegionModel'
import type { UpdateCustomMessageRegionEntity } from '../../domain/entities/UpdateCustomMessageRegionEntity'
import { UpdateCustomMessageRegionModel } from '../models/UpdateCustomMessageRegionModel'
import type { UpdateCustomMessageDistrictEntity } from '../../domain/entities/UpdateCustomMessageDistrictEntity'
import { UpdateCustomMessageDistrictModel } from '../models/UpdateCustomMessageDistrictModel'
import type { RegionMiniEntity } from '@/features/region/domain/entities/RegionMiniEntity'

export class CustomMessageRepositoryImpl implements CustomMessageRepository {
  async getCustomMessage(
    filterData: CustomMessageFilterEntity
  ): Promise<CustomMessagesResponseEntity | { error: true }> {
    const customMessageFilterModel = new CustomMessageFilterModel(filterData)
    try {
      const response =
        await CustomMessagesService.listAdminCustomMessagesAdmin(customMessageFilterModel)
      const customMessageResponseModel = new CustomMessagesResponseModel(response)
      return customMessageResponseModel
    } catch {
      return { error: true }
    }
  }

  async getCustomMessageById(id: number): Promise<CustomMessageEntity | { error: true }> {
    try {
      const response = await CustomMessagesService.getCustomMessageAdmin({
        id
      })

      const responseModel = new CustomMessageModel(response)
      return responseModel
    } catch {
      return { error: true }
    }
  }

  async createCustomMessage(
    data: CreateUpdateCustomMessageEntity
  ): Promise<{ success: true } | { error: true }> {
    const createCustomMessageModel = new CreateUpdateCustomMessageModel(data)
    try {
      await CustomMessagesService.createCustomMessageAdmin({
        requestBody: createCustomMessageModel as PostCustomMessageRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async updateCustomMessage(
    id: number,
    data: CreateUpdateCustomMessageEntity
  ): Promise<{ success: true } | { error: true }> {
    const updateCustomMessageModel = new CreateUpdateCustomMessageModel(data)
    try {
      await CustomMessagesService.updateCustomMessageAdmin({
        id,
        requestBody: updateCustomMessageModel as PutCustomMessageRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
 
  async deleteCustomMessage(id: number): Promise<{ isSuccess: boolean }> {
    try {
      await CustomMessagesService.deleteCustomMessageAdmin({ id })
      return { isSuccess: true }
    } catch (error) {
      return { isSuccess: false }
    }
  }
  
  async getCustomMessageUsers(customMessageId: number): Promise<UserMiniEntity[] | { error: true }> {
    try {
        const response = await CustomMessagesService.customMessageUsersAdmin({ id: customMessageId })
        const userMiniModels = response.users?.map((item) => new UserMiniModel(item as UserMiniEntity))
        return userMiniModels ?? []
      } catch {
        return { error: true }
      }
    }

    async updateCustomMessageUser(
      customMessageId: number,
      customMessageData: CreateUpdateCustomMessageEntity
    ): Promise<{ success: true } | { error: true }> {
      const createUpdateCustomMessageModel = new CreateUpdateCustomMessageModel(customMessageData)
      try {
        await CustomMessagesService.putCustomMessageUsersAdmin({
          id: customMessageId,
          requestBody: {
            userIds: createUpdateCustomMessageModel.userIds
          }
        })
        return { success: true }
      } catch {
        return { error: true }
      }
    }
     
    async getCustomMessageRegions(customMessageId: number): Promise<CustomMessageRegionEntity[] | { error: true }> {
      try {
          // Fetch both regions and districts
          const [regionsResponse, districtsResponse] = await Promise.all([
            CustomMessagesService.customMessageRegionsAdmin({ id: customMessageId }),
            CustomMessagesService.customMessageDistrictsAdmin({ id: customMessageId })
          ])
          
          // Group districts by regionId
          const districtsByRegion = new Map<number, number[]>()
          if (districtsResponse.districts) {
            for (const district of districtsResponse.districts) {
              if (district.id && district.regionId) {
                if (!districtsByRegion.has(district.regionId)) {
                  districtsByRegion.set(district.regionId, [])
                }
                districtsByRegion.get(district.regionId)!.push(district.id)
              }
            }
          }
          
          // Map regions with their selected districts
          const regionMiniModels = regionsResponse.regions?.map((item) => new CustomMessageRegionModel({
            region: item as RegionMiniEntity,
            districtIds: districtsByRegion.get(item.id!) || []
          }))
          return regionMiniModels ?? []
        } catch {
          return { error: true }
        }
      }
  
      async updateCustomMessageRegion(
        customMessageId: number,
        customMessageData: UpdateCustomMessageRegionEntity
      ): Promise<{ success: true } | { error: true }> {
        const updateCustomMessageRegionModel = new UpdateCustomMessageRegionModel(customMessageData)
        try {
          await CustomMessagesService.putCustomMessageRegionsAdmin({
            id: customMessageId,
            requestBody:  updateCustomMessageRegionModel as PutCustomMessageRegionsRequestAdmin 
          })
          return { success: true }
          
        } catch {
          return { error: true }
        }
      }
 
      async updateCustomMessageDistrict(
        customMessageId: number,
        customMessageData: UpdateCustomMessageDistrictEntity
      ): Promise<{ success: true } | { error: true }> {
        const updateCustomMessageDistrictModel = new UpdateCustomMessageDistrictModel(customMessageData)
        try {
          await CustomMessagesService.putCustomMessageDistrictsAdmin({
            id: customMessageId,
            requestBody: updateCustomMessageDistrictModel as PutCustomMessageDistrictsRequestAdmin
          })
          return { success: true }
        } catch {
          return { error: true }
        }
      }

 
}
